import Head from 'next/head';
import type { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '../_app';
import React, { useMemo } from 'react';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import Layout from '@/layouts';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { page3JsonLd } from '@/lib/jsonld';
const WebViewerForInstagramContent = dynamic(
  () => import('@/components/web-viewer-for-instagram'),
  {
    ssr: true,
  },
);

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, req, resolvedUrl } = context;

  const [localeRes, tdkRes] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(resolvedUrl.split('?')[0], locale),
  ]);
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl =
    locale !== 'en' ? `https://${req.headers.host}/${locale}` : `https://${req.headers.host}`;
  const jsonLd = page3JsonLd(
    {
      title: 'Web Viewer for Instagram',
      desc: tdkRes?.data?.description ?? tempData?.head_Viewer_Instagram_Meta_Desc ?? null,
      url: canonicalUrl(req.headers.host, resolvedUrl, locale),
    },
    {
      title: 'Toolkit',
      url: siteUrl + '/toolkit',
    },
    { url: siteUrl },
  );

  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      locale,
      title: tdkRes?.data?.title ?? tempData?.head_Viewer_Instagram_Meta_Title ?? null,
      description: tdkRes?.data?.description ?? tempData?.head_Viewer_Instagram_Meta_Desc ?? null,
      jsonLd,
    },
  };
};

const PageContent: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (
  props,
) => {
  return useMemo(() => <WebViewerForInstagramContent />, []);
};

PageContent.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
          <script
            async
            dangerouslySetInnerHTML={{
              __html: `
                (function(c,l,a,r,i,t,y){
                    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                })(window, document, "clarity", "script", "l9myh0u19b");
              `,
            }}
          />
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default PageContent;
